import React, { useState, useRef, useEffect } from "react";
import CircularProgress from "@mui/material/CircularProgress";
import { getAuth, signInWithCustomToken } from "firebase/auth";

// Firebase imports
import { functions, httpsCallable } from "./firebaseConfig";

const CODE_LENGTH = 4;

const CodeInput = ({ theme, isElectron }) => {
  const [code, setCode] = useState(Array(CODE_LENGTH).fill(""));
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const inputsRef = useRef([]);

  const auth = getAuth();

  const generateLoginCodeToken = httpsCallable(functions, "generateLoginCodeToken");

  useEffect(() => {
    if (inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, []);

  useEffect(() => {
    generateLoginCodeToken({ loginCode: "0000" }).catch(() => {});
  }, [generateLoginCodeToken]);

  useEffect(() => {
    if (isElectron && window.electron && window.electron.onFirebaseLogin) {
      const handleCalendarLogin = (token) => {
        console.log("Received token in calendar window:", token);
        setLoading(true);

        signInWithCustomToken(auth, token)
          .then(() => {
            console.log("Calendar window authenticated with custom token");
            setLoading(false);
          })
          .catch((error) => {
            console.error("Authentication error in calendar window:", error);
            setError(true);
            setLoading(false);
          });
      };

      window.electron.onFirebaseLogin(handleCalendarLogin);

      if (window.ipcRenderer) {
        window.ipcRenderer.on("firebase-login", (event, token) => {
          handleCalendarLogin(token);
        });

        return () => {
          window.ipcRenderer.removeAllListeners("firebase-login");
        };
      }
    }
  }, [auth, isElectron]);

  useEffect(() => {
    if (error && inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  }, [error]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (/^\d*$/.test(value)) {
      const newCode = [...code];
      newCode[index] = value;
      setCode(newCode);
      setError(false);

      if (value && index < CODE_LENGTH - 1) {
        inputsRef.current[index + 1]?.focus();
      } else if (!value && index > 0) {
        inputsRef.current[index - 1]?.focus();
      }

      if (index === CODE_LENGTH - 1 && value) {
        handleSubmit(newCode.join(""));
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "ArrowLeft" && index > 0) {
      inputsRef.current[index - 1]?.focus();
    } else if (e.key === "ArrowRight" && index < CODE_LENGTH - 1) {
      inputsRef.current[index + 1]?.focus();
    } else if (e.key === "Backspace" || e.key === "Delete") {
      if (!code[index] && index > 0) {
        const newCode = [...code];
        newCode[index - 1] = "";
        setCode(newCode);
        inputsRef.current[index - 1]?.focus();
      } else if (e.key === "Backspace" && code[index]) {
        const newCode = [...code];
        newCode[index] = "";
        setCode(newCode);
      }
    }
  };

  const handleSubmit = async (codeValue) => {
    if (!codeValue || codeValue.length !== CODE_LENGTH) {
      setError(true);
      return;
    }
    setLoading(true);
    try {
      const loginToken = await generateLoginCodeToken({ loginCode: codeValue });
      if (loginToken.data.error) {
        setError(true);
        clearInputs();
      } else if (loginToken.data.token) {
        await signInWithCustomToken(auth, loginToken.data.token);

        if (window.location.href.includes("codelogin")) {
          window.location.href = "/";
        }

        console.log("Signed in with custom token");
        clearInputs();
      } else {
        console.log("Unexpected response:", loginToken);
        setError(true);
        clearInputs();
      }
    } catch (error) {
      console.error("Error generating login code token:", error);
      setError(true);
      clearInputs();
    } finally {
      setLoading(false);
    }
  };

  const clearInputs = () => {
    setCode(Array(CODE_LENGTH).fill(""));
    if (inputsRef.current[0]) {
      inputsRef.current[0].focus();
    }
  };

  const inputStyle = {
    width: "50px",
    height: "50px",
    textAlign: "center",
    fontSize: "24px",
    borderRadius: "8px",
    border: `2px solid ${error ? "red" : theme === "dark" ? "#fff" : "#000"}`,
    backgroundColor: "transparent",
    color: theme === "dark" ? "#fff" : "#000",
    outline: "none",
    boxShadow: "none",
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        gap: "10px",
      }}
    >
      {loading ? (
        <CircularProgress color="primary" size={50} />
      ) : (
        code.map((digit, index) => (
          <input
            key={index}
            type="tel"
            value={digit}
            maxLength="1"
            onChange={(e) => handleChange(e, index)}
            onKeyDown={(e) => handleKeyDown(e, index)}
            ref={(el) => (inputsRef.current[index] = el)}
            style={inputStyle}
            inputMode="numeric"
            pattern="[0-9]*"
            autoComplete="one-time-code"
          />
        ))
      )}
    </div>
  );
};

export default CodeInput;
